import { create } from 'zustand'
import type { Session, User } from '@supabase/supabase-js'
import { supabase } from '../lib/supabase'
import { analytics } from '../lib/analytics'
import { useTerminalStore } from './terminalStore'

function applyUserTheme(user: User | null | undefined) {
  const t = user?.user_metadata?.theme
  if (t !== 'dark' && t !== 'light') return
  // Apply directly without going through setTheme to avoid the
  // setTheme → supabase.auth.updateUser → onAuthStateChange → applyUserTheme loop
  const current = useTerminalStore.getState().theme
  if (current === t) return
  document.documentElement.classList.toggle('light', t === 'light')
  useTerminalStore.setState({ theme: t })
}

interface AuthState {
  session: Session | null
  user: User | null
  loading: boolean
  setSession: (s: Session | null) => void
  signOut: () => Promise<void>
  init: () => () => void
}

export const useAuthStore = create<AuthState>((set) => ({
  session: null,
  user: null,
  loading: true,

  setSession: (s) => set({ session: s, user: s?.user ?? null }),

  signOut: async () => {
    await supabase.auth.signOut()
    analytics.reset()
    set({ session: null, user: null })
  },

  init: () => {
    // Initial session
    supabase.auth.getSession().then(({ data }) => {
      set({ session: data.session, user: data.session?.user ?? null, loading: false })
      applyUserTheme(data.session?.user)
    })

    // Subscribe to changes
    const { data: { subscription } } = supabase.auth.onAuthStateChange((_event, session) => {
      set({ session, user: session?.user ?? null, loading: false })
      if (session?.user) {
        analytics.identify(session.user.id)
        applyUserTheme(session.user)
      }
    })

    return () => subscription.unsubscribe()
  },
}))
